import { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Tag, TrendingUp, ShoppingCart, CreditCard, Building, Plane } from 'lucide-react';

const ReportTable = ({ headers, data, keyPrefix, loading }) => (
  <div className="overflow-x-auto max-h-[600px]">
    <table className="w-full min-w-[600px]">
      <thead className="bg-gray-50 border-b">
        <tr>
          {headers.map((header) => (
            <th key={header.key} className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase ${header.numeric ? 'text-right' : ''}`}>
              {header.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y">
        {loading ? (
          <tr>
            <td colSpan={headers.length} className="text-center p-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </td>
          </tr>
        ) : data.length === 0 ? (
          <tr>
            <td colSpan={headers.length} className="text-center p-6 text-gray-500">
              Nenhum dado encontrado para este período.
            </td>
          </tr>
        ) : (
          data.map((row, rowIndex) => (
            <tr key={`${keyPrefix}-${rowIndex}`} className="hover:bg-gray-50">
              {headers.map((header) => (
                <td key={header.key} className={`px-4 py-3 text-sm ${header.numeric ? 'text-right font-medium' : 'text-gray-700'}`}>
                  {header.format ? header.format(row[header.key]) : row[header.key]}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const formatCurrency = (value) => {
  const numberValue = Number(value) || 0; 
  return `R$ ${numberValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

const capitalize = (s) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const getCurrentMonthParams = () => {
  const today = new Date(); 
  const year = today.getFullYear();
  const month = today.getMonth();
  
  const from = new Date(year, month, 1); 
  const to = new Date(year, month + 1, 0); 
  
  const params = {};
  params.data_inicio = new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0)).toISOString();
  params.data_fim = new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999)).toISOString();
  
  return { params, dateRange: { from, to } };
};

export default function Relatorios() {
  const [filters, setFilters] = useState({
    date: getCurrentMonthParams().dateRange,
  });
  const [activeTab, setActiveTab] = useState('vendas_vendedor');
  const [reportLoading, setReportLoading] = useState(false); 
  
  // Estado inicial limpo
  const initialState = {
    vendas_vendedor: [],
    despesas_categoria: [],
    top_clientes: [],
    top_fornecedores: [],
    top_rotas: [],
    vendas_produto: [],
    formas_pagamento: []
  };

  const [reportData, setReportData] = useState(initialState);

  useEffect(() => {
    loadReportData('vendas_vendedor', filters);
  }, []); 

  const loadReportData = async (tab = activeTab, currentFilters = filters) => {
    setReportLoading(true);
    
    const params = {};
    if (currentFilters.date?.from) {
        const localDate = new Date(currentFilters.date.from);
        const startDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0));
        params.data_inicio = startDate.toISOString();
    }
    if (currentFilters.date?.to) {
        const localDate = new Date(currentFilters.date.to);
        const endDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 23, 59, 59, 999));
        params.data_fim = endDate.toISOString();
    }

    let endpoint = '';
    switch (tab) {
      case 'vendas_vendedor': endpoint = '/relatorios/ranking-vendedores'; break;
      case 'despesas_categoria': endpoint = '/relatorios/despesas-categoria'; break;
      case 'top_clientes': endpoint = '/relatorios/top-clientes'; break;
      case 'top_fornecedores': endpoint = '/relatorios/top-fornecedores'; break;
      case 'top_rotas': endpoint = '/relatorios/top-rotas'; break;
      case 'vendas_produto': endpoint = '/relatorios/vendas-produto'; break;
      case 'formas_pagamento': endpoint = '/relatorios/formas-pagamento'; break;
      default: setReportLoading(false); return;
    }

    try {
      const res = await axiosInstance.get(endpoint, { params });
      setReportData(prev => ({
        ...prev,
        [tab]: res.data 
      }));
    } catch (error) {
      toast.error(`Erro ao carregar relatório: ${tab}`);
    } finally {
      setReportLoading(false);
    }
  };
  
  const handleFiltrarClick = () => {
     // Ao filtrar, limpamos o "cache" das outras abas para forçar o recarregamento 
     // delas com as novas datas assim que o usuário clicar nelas.
     setReportData(initialState);
     loadReportData(activeTab, filters);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Se a aba estiver vazia (porque o cache foi limpo no Filtro ou é o 1º acesso), busca os dados
    if (reportData[newTab]?.length === 0) {
      loadReportData(newTab, filters);
    }
  };

  return (
    <div>
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 pt-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Relatórios</h1>
          <p className="text-gray-600">Analise a performance da sua agência.</p>
        </div>

        <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label>Período</Label>
              <DateRangePicker
                startDate={filters.date.from}
                endDate={filters.date.to}
                onChange={({ startDate, endDate }) => {
                  setFilters({ ...filters, date: { from: startDate, to: endDate } });
                }}
                placeholder="Selecione o período"
                className="mt-1"
              />
            </div>
            
            <div className="flex items-end">
              <Button data-testid="filter-apply" onClick={handleFiltrarClick} className="w-full" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                Filtrar Período
              </Button>
            </div>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {/* Menu de Abas flexível para acomodar as 7 opções sem espremer */}
          <TabsList className="flex flex-wrap h-auto gap-2 mb-8 bg-gray-50 p-2 rounded-xl justify-start">
            <TabsTrigger value="vendas_vendedor" className="flex-grow md:flex-grow-0"><Users className="w-4 h-4 mr-2"/>Vendas por Vendedor</TabsTrigger>
            <TabsTrigger value="despesas_categoria" className="flex-grow md:flex-grow-0"><Tag className="w-4 h-4 mr-2"/>Despesas por Categoria</TabsTrigger>
            <TabsTrigger value="top_clientes" className="flex-grow md:flex-grow-0"><TrendingUp className="w-4 h-4 mr-2"/>Top Clientes</TabsTrigger>
            <TabsTrigger value="top_fornecedores" className="flex-grow md:flex-grow-0"><Building className="w-4 h-4 mr-2"/>Top Fornecedores</TabsTrigger>
            <TabsTrigger value="top_rotas" className="flex-grow md:flex-grow-0"><Plane className="w-4 h-4 mr-2"/>Top Rotas</TabsTrigger>
            <TabsTrigger value="vendas_produto" className="flex-grow md:flex-grow-0"><ShoppingCart className="w-4 h-4 mr-2"/>Top Serviço</TabsTrigger>
            <TabsTrigger value="formas_pagamento" className="flex-grow md:flex-grow-0"><CreditCard className="w-4 h-4 mr-2"/>Formas de Pagamento</TabsTrigger>
          </TabsList>

          <TabsContent value="vendas_vendedor">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Relatório de Vendas por Vendedor</h3>
              <ReportTable
                keyPrefix="vendedor"
                headers={[
                  { label: 'Vendedor', key: 'nome' },
                  { label: 'Quantidade', key: 'quantidade', numeric: true },
                  { label: 'Receita (R$)', key: 'valor', numeric: true, format: formatCurrency },
                  { label: 'Lucro (R$)', key: 'lucro_total', numeric: true, format: formatCurrency },
                ]}
                data={reportData.vendas_vendedor}
                loading={reportLoading}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="despesas_categoria">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Relatório de Despesas por Categoria</h3>
              <ReportTable
                keyPrefix="despesa"
                headers={[
                  { label: 'Categoria', key: 'categoria', format: capitalize },
                  { label: 'Valor Total (R$)', key: 'valor', numeric: true, format: formatCurrency },
                ]}
                data={reportData.despesas_categoria}
                loading={reportLoading}
              />
            </div>
          </TabsContent>

          <TabsContent value="top_clientes">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Top Clientes</h3>
              <ReportTable
                keyPrefix="cliente"
                headers={[
                  { label: 'Cliente', key: 'nome' },
                  { label: 'Valor Total (R$)', key: 'valor', numeric: true, format: formatCurrency },
                ]}
                data={reportData.top_clientes}
                loading={reportLoading}
              />
            </div>
          </TabsContent>

          <TabsContent value="top_fornecedores">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Top Fornecedores</h3>
              <ReportTable
                keyPrefix="fornecedor-report"
                headers={[
                  { label: 'Fornecedor', key: 'nome' },
                  { label: 'Quantidade Vendas', key: 'quantidade', numeric: true },
                  { label: 'Valor Total (R$)', key: 'valor', numeric: true, format: formatCurrency },
                ]}
                data={reportData.top_fornecedores}
                loading={reportLoading}
              />
            </div>
          </TabsContent>

          <TabsContent value="top_rotas">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Top Rotas (Destinos)</h3>
              <ReportTable
                keyPrefix="rota"
                headers={[
                  { label: 'Rota', key: 'rota' },
                  { label: 'Quantidade', key: 'quantidade', numeric: true },
                ]}
                data={reportData.top_rotas}
                loading={reportLoading}
              />
            </div>
          </TabsContent>

          <TabsContent value="vendas_produto">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Performance por Produto / Serviço</h3>
              <ReportTable
                keyPrefix="produto"
                headers={[
                  { label: 'Serviço', key: 'servico', format: capitalize },
                  { label: 'Volume Faturado (R$)', key: 'receita', numeric: true, format: formatCurrency },
                  { label: 'Custo Total (R$)', key: 'custo', numeric: true, format: formatCurrency },
                  { label: 'Lucro (R$)', key: 'lucro', numeric: true, format: formatCurrency },
                ]}
                data={reportData.vendas_produto}
                loading={reportLoading}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="formas_pagamento">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Vendas por Forma de Pagamento</h3>
              <ReportTable
                keyPrefix="pagamento"
                headers={[
                  { label: 'Forma de Pagamento', key: 'forma' },
                  { label: 'Quantidade', key: 'quantidade', numeric: true },
                  { label: 'Valor Total (R$)', key: 'valor', numeric: true, format: formatCurrency },
                ]}
                data={reportData.formas_pagamento}
                loading={reportLoading}
              />
            </div>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}